import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { ArrowUp, LockKeyhole, MessageSquare } from "lucide-react";
import { Question, Topic, questionApi } from "./api";
import "./questions.css";
import LocationFields from "./LocationFields";
import {
  Location,
  US_STATES,
  guessLocation,
  guessTopic,
  toJurisdiction,
} from "./intake";
import { formatPrice, useReviewPrice } from "../Reviews/reviewPrice";
import { usePortal } from "../Portal/PortalContext";
import { useQuestionDraft } from "./useQuestionDraft";
import { discardGuestDraft, readGuestDraft } from "../Public/guestDraft";

// A readable title from the first message: whole words, at most 80 characters.
export function questionTitle(message: string, max = 80) {
  const text = message.trim().replace(/\s+/g, " ");
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s.,;:!?-]+$/, "")}…`;
}

export default function NewQuestion() {
  const navigate = useNavigate();
  const { access } = usePortal();
  const beta = Boolean(access?.targetedBeta);
  const recovery = useQuestionDraft();
  const [guest, setGuest] = useState(readGuestDraft);
  const [message, setMessage] = useState("");
  const [show, setShow] = useState(false);
  const [options, setOptions] = useState<{ topics: Topic[]; terms?: any }>();
  const [topic, setTopic] = useState("contracts");
  // Once the customer picks a topic, stop guessing it from the message.
  const [topicChosen, setTopicChosen] = useState(false);
  const [locationChosen, setLocationChosen] = useState(false);
  const [location, setLocation] = useState<Location>({
    country: "US",
    region: "California",
  });
  const [accepted, setAccepted] = useState(false);
  const [acceptHint, setAcceptHint] = useState(false);
  const reviewPrice = useReviewPrice();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const creationKey = useRef(crypto.randomUUID());
  const draft = useRef<Question | null>(null);
  const activationMessage = useRef<string | undefined>();
  const locked = useRef(false);
  const terms = options?.terms;
  useLayoutEffect(() => {
    const d = recovery.recovered;
    setMessage(d?.message || "");
    setTopic(d?.topic || "contracts");
    setLocation(d?.location || { country: "US", region: "California" });
    setTopicChosen(Boolean(d));
    setLocationChosen(Boolean(d));
    setShow(false);
    setAccepted(false);
    creationKey.current = d?.creationKey || crypto.randomUUID();
    draft.current = d?.questionId ? ({ id: d.questionId } as Question) : null;
    activationMessage.current = d?.activationMessage;
  }, [recovery.recovered, recovery.key]);
  function changeMessage(value: string) {
    setMessage(value);
    recovery.remember({ message: value, topic, location });
  }
  function resumeGuest() {
    if (!guest) return;
    const validTopic = options?.topics.some(t => t.id === guest.topic);
    const nextTopic = validTopic ? guest.topic : "contracts";
    const validLocation =
      ["US", "CA", "GB"].includes(guest.country) &&
      (guest.country !== "US" || US_STATES.includes(guest.region));
    const nextLocation = {
      country: validLocation ? guest.country : "US",
      region: validLocation ? guest.region : "",
    };
    setMessage(guest.text);
    setTopic(nextTopic);
    setLocation(nextLocation);
    setTopicChosen(Boolean(validTopic));
    setLocationChosen(validLocation);
    recovery.remember({
      message: guest.text,
      topic: nextTopic,
      location: nextLocation,
    });
    discardGuestDraft();
    setGuest(null);
  }
  useEffect(() => {
    const c = new AbortController();
    setError("");
    setAccepted(false);
    questionApi<{ topics: Topic[]; terms?: any }>(
      "/options",
      "GET",
      undefined,
      c.signal
    )
      .then(setOptions)
      .catch(e => {
        if (!c.signal.aborted) setError(e.message);
      });
    return () => c.abort();
  }, [reload]);

  function openSetup() {
    if (!message.trim()) return;
    if (!topicChosen)
      setTopic(
        guessTopic(
          message,
          (options?.topics || []).map(t => t.id)
        )
      );
    if (!locationChosen) {
      const suggested = guessLocation(message);
      if (suggested) setLocation(suggested);
      else if (
        ["patent", "trademark"].includes(
          guessTopic(
            message,
            (options?.topics || []).map(t => t.id)
          )
        )
      )
        setLocation({ country: "US", region: "" });
    }
    setShow(true);
  }

  async function start(e: React.FormEvent) {
    e.preventDefault();
    if (!accepted) {
      setAcceptHint(true);
      return;
    }
    if (locked.current || !message.trim() || !terms) return;
    locked.current = true;
    setBusy(true);
    setError("");
    try {
      recovery.remember({
        message,
        topic,
        location,
        creationKey: creationKey.current,
        ...(draft.current ? { questionId: draft.current.id } : {}),
      });
      const intake = {
        title: questionTitle(message),
        topicKey: topic,
        jurisdiction: toJurisdiction(topic, location),
      };
      const resetSavedQuestion = () => {
        draft.current = null;
        creationKey.current = crypto.randomUUID();
        activationMessage.current = undefined;
        recovery.remember({
          message,
          topic,
          location,
          creationKey: creationKey.current,
          questionId: undefined,
          activationMessage: undefined,
        });
      };
      // Recover a committed activation, but never attach changed input to an old question.
      if (draft.current) {
        let current: { question: Question };
        try {
          current = await questionApi<{ question: Question }>(
            `/${draft.current.id}`
          );
        } catch (error: any) {
          if (error.status === 404) {
            resetSavedQuestion();
            throw new Error(
              "The saved question is no longer available. Your message is preserved. Accept the notice again to start a new question."
            );
          }
          throw error;
        }
        const q = current.question;
        if (q.status === "archived") {
          resetSavedQuestion();
          throw new Error(
            "The saved question was archived. Your message is preserved. Accept the notice again to start a new question."
          );
        }
        if (
          q.status === "active" &&
          ((activationMessage.current !== undefined &&
            activationMessage.current !== message) ||
            (q.title !== undefined && q.title !== intake.title) ||
            (q.topicKey !== undefined && q.topicKey !== intake.topicKey) ||
            (q.jurisdiction !== undefined &&
              JSON.stringify([
                q.jurisdiction.level,
                (q.jurisdiction as any).countryCode,
                (q.jurisdiction as any).region,
              ]) !==
                JSON.stringify([
                  intake.jurisdiction.level,
                  (intake.jurisdiction as any).countryCode,
                  (intake.jurisdiction as any).region,
                ])))
        ) {
          resetSavedQuestion();
          throw new Error(
            "The previous question is already active with different details and remains in My questions. Your edited message is preserved. Accept the notice again to start a new question."
          );
        }
        draft.current = q;
      }
      const result =
        draft.current?.status === "active"
          ? { question: draft.current }
          : await questionApi<{ question: Question }>(
              draft.current ? `/${draft.current.id}` : "",
              draft.current ? "PUT" : "POST",
              {
                ...intake,
                ...(draft.current
                  ? { version: draft.current.version }
                  : { creationKey: creationKey.current }),
              }
            );
      draft.current = result.question;
      recovery.remember({
        message,
        topic,
        location,
        creationKey: creationKey.current,
        questionId: result.question.id,
        activationMessage: message,
      });
      activationMessage.current = message;
      await questionApi(`/${result.question.id}/activate`, "POST", {
        version: result.question.version,
        termsVersion: terms.version,
        termsHash: terms.hash,
        accepted: true,
      });
      recovery.remember({ message: "", topic, location });
      navigate(`/questions/${result.question.id}`, {
        replace: true,
        state: {
          firstMessage: {
            questionId: result.question.id,
            text: message.trim(),
          },
        },
      });
    } catch (e: any) {
      setError(e.message);
      // A changed notice must be displayed and accepted again.
      setAccepted(false);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="new-question-page">
      {guest && (
        <Alert variant="info">
          <p>
            A chat draft remains in this browser tab. Only continue if it is
            yours. It has not been sent.
          </p>
          <Button disabled={!options} onClick={resumeGuest}>
            Use my draft
          </Button>{" "}
          <Button
            variant="light"
            onClick={() => {
              discardGuestDraft();
              setGuest(null);
            }}
          >
            Discard draft
          </Button>
        </Alert>
      )}
      {!recovery.saved && (
        <Alert variant="warning">
          This browser could not save your draft. Keep a copy before leaving.
        </Alert>
      )}
      <div className="new-question-intro">
        <span className="new-question-mark">
          <MessageSquare size={24} />
        </span>
        <p className="reviewed-eyebrow">AI ANSWERS. ATTORNEY JUDGMENT.</p>
        <h1>If it matters, get it reviewed.</h1>
        <p>
          Start with an AI conversation. When you need professional judgment,
          request an attorney’s written answer.
        </p>
      </div>
      <Form
        className="new-question-composer"
        onSubmit={e => {
          e.preventDefault();
          openSetup();
        }}
      >
        <Form.Control
          as="textarea"
          rows={4}
          aria-label="Your message"
          autoFocus
          placeholder="Ask a legal question, or paste an AI answer you want to discuss…"
          value={message}
          maxLength={20000}
          onChange={e => changeMessage(e.target.value)}
          onKeyDown={e => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.nativeEvent.isComposing
            ) {
              e.preventDefault();
              openSetup();
            }
          }}
        />
        <div className="new-question-composer-footer">
          <span>Enter to send · Shift+Enter for a new line</span>
          <Button
            type="submit"
            disabled={!message.trim()}
            aria-label="Send message"
          >
            <ArrowUp size={21} />
          </Button>
        </div>
      </Form>
      <p className="new-question-footnote">
        {beta
          ? "Test beta: use generic examples. No charges or legal services are provided. The simulated fee is $150."
          : "AI answers are free."}
        {!beta &&
          reviewPrice &&
          ` Want an attorney to check one? ${formatPrice(
            reviewPrice.priceCents
          )}, answered within ${
            reviewPrice.turnaroundHours
          } hours after the agreed review starts. Matching comes first.`}
      </p>
      <Modal
        show={show}
        onHide={() => !busy && setShow(false)}
        centered
        size="lg"
        scrollable
        className="question-setup-modal"
      >
        <Modal.Header closeButton={!busy}>
          <Modal.Title>Before we begin</Modal.Title>
        </Modal.Header>
        <Form onSubmit={start}>
          <Modal.Body>
            <p className="setup-intro">
              Check the suggested topic and location before sending. These help
              an attorney understand where your question applies.
            </p>
            {error && (
              <Alert variant="danger">
                {error}{" "}
                <Button variant="link" onClick={() => setReload(n => n + 1)}>
                  Reload setup
                </Button>
              </Alert>
            )}
            <fieldset disabled={busy}>
              <div className="question-setup-fields">
                <Form.Group controlId="start-topic">
                  <Form.Label>Area of law</Form.Label>
                  <Form.Select
                    value={topic}
                    onChange={e => {
                      setTopic(e.target.value);
                      recovery.remember({
                        message,
                        topic: e.target.value,
                        location,
                      });
                      setTopicChosen(true);
                    }}
                  >
                    {(
                      options?.topics || [
                        { id: "other", label: "Other / not sure" },
                      ]
                    ).map(t => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text>
                    For an agreement or ownership clause, choose Contracts. If
                    you’re unsure, choose Other / not sure.
                  </Form.Text>
                </Form.Group>
                <LocationFields
                  idPrefix="start"
                  value={location}
                  onChange={value => {
                    setLocation(value);
                    recovery.remember({ message, topic, location: value });
                    setLocationChosen(true);
                  }}
                />
              </div>
              <section
                className="question-privacy"
                aria-label="Engagement and privacy"
              >
                <h2>
                  <LockKeyhole size={18} /> Terms
                </h2>
                <p>
                  AI answers aren’t checked by an attorney and aren’t legal
                  advice.{" "}
                  {beta
                    ? "No payments or real legal services are provided in this test beta."
                    : "Nothing is charged unless you ask for an attorney review."}
                </p>
                {terms ? (
                  <>
                    <details className="question-engagement-details">
                      <summary>Read the full terms</summary>
                      <div className="question-engagement-letter">
                        <strong>{terms.title}</strong>
                        <p>{terms.text}</p>
                      </div>
                    </details>
                    <Form.Check
                      id="start-acceptance"
                      checked={accepted}
                      onChange={e => {
                        setAccepted(e.target.checked);
                        if (e.target.checked) setAcceptHint(false);
                      }}
                      label={
                        terms.testOnly
                          ? "I have read and accept this test-beta notice."
                          : "I have read and accept the engagement letter."
                      }
                    />
                    {acceptHint && (
                      <p className="text-danger small mt-2" role="alert">
                        Tick the box to continue.
                      </p>
                    )}
                  </>
                ) : (
                  <p role="status">
                    {options
                      ? "Engagement setup is not available yet. Your message has not been sent."
                      : "Loading engagement terms…"}
                  </p>
                )}
              </section>
            </fieldset>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              disabled={busy}
              onClick={() => setShow(false)}
            >
              Back to message
            </Button>
            <Button type="submit" disabled={!terms || busy}>
              {busy ? "Opening conversation…" : "Agree & send message"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </main>
  );
}
